import { createContext, useContext, useState, useMemo, useRef, useCallback } from "react";

const TourContext = createContext(null);

export function TourProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  
  // Use refs to ensure we always have the latest values
  const completionKeyRef = useRef(null);
  const stepsRef = useRef([]);

  const currentStep = steps[stepIndex] || null;

  // Keep stepsRef in sync with steps state
  stepsRef.current = steps;

  const endTour = useCallback(() => {
    setIsOpen(false);
    
    // Use ref to ensure we get the most current value
    const keyToSave = completionKeyRef.current;
    
    // If a completion key was provided, mark it as complete in localStorage
    if (keyToSave) {
      localStorage.setItem(keyToSave, "true");
      completionKeyRef.current = null; // Clear ref after saving
    }
    
    setSteps([]);
    stepsRef.current = []; // Clear ref
    setStepIndex(0);
  }, []);

  const startTour = useCallback((newSteps, localStorageKey = null) => {
    setSteps(newSteps);
    stepsRef.current = newSteps; // Update ref immediately
    setStepIndex(0);
    setIsOpen(true);
    completionKeyRef.current = localStorageKey; // Store in ref immediately
  }, []);

  const next = useCallback(() => {
    setStepIndex((i) => {
      const currentSteps = stepsRef.current;
      if (i < currentSteps.length - 1) {
        return i + 1;
      } else {
        // End tour when reaching last step
        setTimeout(() => {
          endTour();
        }, 0);
        return i;
      }
    });
  }, [endTour]);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      currentStep,
      stepIndex,
      steps,
      startTour,
      next,
      back,
      endTour,
    }),
    [isOpen, currentStep, stepIndex, steps, startTour, next, back, endTour]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
}

export const useTourContext = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used inside TourProvider");
  return ctx;
};
